import { useId, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";
import { ToneMarker } from "../status/StatusBadge";

/**
 * Form field: visible label, optional hint, error as text under the control (never colour alone).
 * Wraps the control in a <label>, so a single child input is labelled without ids. For composite controls
 * pass `htmlFor` and the matching `id` on the control instead.
 */
export function Field({ label, hint, error, required, children, className, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; required?: boolean; children: ReactNode; className?: string; htmlFor?: string }) {
  const Tag = htmlFor ? "div" : "label";
  return (
    <Tag className={clsx("block", className)} {...(htmlFor ? {} : {})}>
      {label && (
        <span className="mb-1.5 block text-[13.5px] font-semibold text-fg">
          {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
          {required && <span className="ml-1 font-normal text-fg-3">(required)</span>}
        </span>
      )}
      {children}
      {hint && !error && <span className="mt-1.5 block text-small text-fg-3">{hint}</span>}
      {error && (
        <span className="mt-1.5 flex items-start gap-1.5 text-small font-semibold text-bad" role="alert">
          <span className="mt-[4px] inline-flex"><ToneMarker tone="warn" /></span>{error}
        </span>
      )}
    </Tag>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> { invalid?: boolean; mono?: boolean }
export function Input({ className, invalid, mono, ...rest }: InputProps) {
  return <input className={clsx("iga-input", mono && "font-mono text-[13px]", className)} aria-invalid={invalid || undefined} {...rest} />;
}

export function Textarea({ className, invalid, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea className={clsx("iga-input min-h-24", className)} aria-invalid={invalid || undefined} {...rest} />;
}

export function Select({ className, children, invalid, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return <select className={clsx("iga-input", className)} aria-invalid={invalid || undefined} {...rest}>{children}</select>;
}

/** 18 px checkbox with the brand accent and a visible label. */
export function Checkbox({ label, className, description, id, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; description?: ReactNode }) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <label htmlFor={cid} className={clsx("inline-flex items-start gap-2.5 text-fg", rest.disabled && "opacity-60", className)}>
      <input id={cid} type="checkbox" className="mt-0.5 h-[18px] w-[18px] flex-none rounded-tag accent-brand" {...rest} />
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block">{label}</span>}
          {description && <span className="block text-small text-fg-3">{description}</span>}
        </span>
      )}
    </label>
  );
}

/** Search input with the icon slot and an optional keyboard hint ("/"). */
export function SearchInput({ className, hint, ...rest }: InputProps & { hint?: string }) {
  return (
    <span className={clsx("relative block", className)}>
      <svg aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      <Input type="search" className={clsx("pl-9", hint && "pr-9")} {...rest} />
      {hint && <kbd aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-fg-3">{hint}</kbd>}
    </span>
  );
}
